/**
 * Component: ExtendedPerformanceCards
 * Author: Dine AI
 * Last Modified: December 17, 2025 - Light mode conversion
 */
import React, { useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { TrendingUp, Clock, DollarSign, Package, Info, Sparkles } from 'lucide-react';
import apiClient from '@/services/api/client';

interface ExtendedPerformanceData {
  summary?: {
    revenue_generated?: number;
    total_orders?: number;
    avg_order_value?: number;
    staff_hours_saved?: number;
  };
  date_range?: {
    start?: string;
    end?: string;
  };
}

interface ExtendedPerformanceCardsProps {
  monthlyData?: ExtendedPerformanceData;
  quarterlyData?: ExtendedPerformanceData;
  combinedMonthlyData?: ExtendedPerformanceData;
  combinedQuarterlyData?: ExtendedPerformanceData;
  hourlyRate?: number;
}

// Animated counter component
const AnimatedCounter = ({ value, prefix = '', suffix = '', decimals = 0, duration = 2 }: any) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let startTime: number | null = null;
    const startValue = 0;
    const endValue = value || 0;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      
      // Easing function for smooth animation
      const easeOutExpo = 1 - Math.pow(2, -10 * progress);
      const currentValue = startValue + (endValue - startValue) * easeOutExpo;
      
      setDisplayValue(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration]);

  return (
    <span>
      {prefix}{displayValue.toLocaleString(undefined, { 
        minimumFractionDigits: decimals, 
        maximumFractionDigits: decimals 
      })}{suffix}
    </span>
  );
};

const formatDateRange = (start?: string, end?: string): string => {
  if (!start || !end) return '';
  
  const startDate = new Date(start);
  const endDate = new Date(end);
  
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

export function ExtendedPerformanceCards({ 
  monthlyData, 
  quarterlyData,
  combinedMonthlyData,
  combinedQuarterlyData,
  hourlyRate = 20
}: ExtendedPerformanceCardsProps) {
  
  const controls = useAnimation();
  const [calculatedCombinedData, setCalculatedCombinedData] = useState<{
    monthly: ExtendedPerformanceData | null;
    quarterly: ExtendedPerformanceData | null;
  }>({ monthly: null, quarterly: null });
  
  useEffect(() => {
    controls.start({ opacity: 1, y: 0 });
  }, []);
  
  // Fetch and calculate combined data on mount
  useEffect(() => {
    const fetchCombinedData = async () => {
      try {
        console.log('📊 Fetching combined data for annual projection (frontend)...');
        
        // First, get the active seasonal menu ID
        let seasonalMenuId = null;
        try {
          const seasonalMenusRes = await apiClient.get('/seasonal-menus');
          const menus = Array.isArray(seasonalMenusRes.data) 
            ? seasonalMenusRes.data 
            : seasonalMenusRes.data.seasonal_menus || [];
          
          const activeMenu = menus
            .filter((menu: any) => menu.is_active)
            .sort((a: any, b: any) => 
              new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            )[0];
          
          if (activeMenu) {
            seasonalMenuId = activeMenu.seasonal_menu_id;
            console.log('Found active seasonal menu:', seasonalMenuId);
          }
        } catch (err) {
          console.log('No seasonal menu found or error fetching');
        }
        
        // Fetch all data in parallel
        const [regular30, regular90, seasonal30, seasonal90] = await Promise.all([
          apiClient.get('/analytics/dine-ai-impact', { 
            params: { menu_type: 'regular', days: 30 } 
          }).catch(() => null),
          apiClient.get('/analytics/dine-ai-impact', { 
            params: { menu_type: 'regular', days: 90 } 
          }).catch(() => null),
          seasonalMenuId ? apiClient.get('/analytics/dine-ai-impact', { 
            params: { menu_type: 'seasonal', days: 30, seasonal_menu_id: seasonalMenuId } 
          }).catch(() => null) : null,
          seasonalMenuId ? apiClient.get('/analytics/dine-ai-impact', { 
            params: { menu_type: 'seasonal', days: 90, seasonal_menu_id: seasonalMenuId } 
          }).catch(() => null) : null
        ]);
        
        // Combine 30-day data
        const combined30 = {
          summary: {
            revenue_generated: 
              (regular30?.data?.dine_ai_impact?.summary?.revenue_generated || regular30?.data?.summary?.revenue_generated || 0) +
              (seasonal30?.data?.dine_ai_impact?.summary?.revenue_generated || seasonal30?.data?.summary?.revenue_generated || 0),
            total_orders: 
              (regular30?.data?.dine_ai_impact?.summary?.total_orders || regular30?.data?.summary?.total_orders || 0) +
              (seasonal30?.data?.dine_ai_impact?.summary?.total_orders || seasonal30?.data?.summary?.total_orders || 0),
            staff_hours_saved: 
              (regular30?.data?.dine_ai_impact?.summary?.staff_hours_saved || regular30?.data?.summary?.staff_hours_saved || 0) +
              (seasonal30?.data?.dine_ai_impact?.summary?.staff_hours_saved || seasonal30?.data?.summary?.staff_hours_saved || 0),
            avg_order_value: 0
          }
        };
        
        // Calculate combined AOV
        if (combined30.summary.total_orders > 0) {
          combined30.summary.avg_order_value = 
            combined30.summary.revenue_generated / combined30.summary.total_orders;
        }
        
        // Combine 90-day data
        const combined90 = {
          summary: {
            revenue_generated: 
              (regular90?.data?.dine_ai_impact?.summary?.revenue_generated || regular90?.data?.summary?.revenue_generated || 0) +
              (seasonal90?.data?.dine_ai_impact?.summary?.revenue_generated || seasonal90?.data?.summary?.revenue_generated || 0),
            total_orders: 
              (regular90?.data?.dine_ai_impact?.summary?.total_orders || regular90?.data?.summary?.total_orders || 0) +
              (seasonal90?.data?.dine_ai_impact?.summary?.total_orders || seasonal90?.data?.summary?.total_orders || 0),
            staff_hours_saved: 
              (regular90?.data?.dine_ai_impact?.summary?.staff_hours_saved || regular90?.data?.summary?.staff_hours_saved || 0) +
              (seasonal90?.data?.dine_ai_impact?.summary?.staff_hours_saved || seasonal90?.data?.summary?.staff_hours_saved || 0),
            avg_order_value: 0
          }
        };
        
        // Calculate combined AOV
        if (combined90.summary.total_orders > 0) {
          combined90.summary.avg_order_value = 
            combined90.summary.revenue_generated / combined90.summary.total_orders;
        }
        
        const regularHours = regular90?.data?.dine_ai_impact?.summary?.staff_hours_saved || regular90?.data?.summary?.staff_hours_saved || 0;
        const seasonalHours = seasonal90?.data?.dine_ai_impact?.summary?.staff_hours_saved || seasonal90?.data?.summary?.staff_hours_saved || 0;
        
        console.log('✅ Frontend combined data calculated:', {
          regular90Hours: regularHours,
          seasonal90Hours: seasonalHours,
          combined90Hours: combined90.summary.staff_hours_saved,
          annualHours: combined90.summary.staff_hours_saved * 4,
          annualProjection: combined90.summary.staff_hours_saved * 4 * 20,
          calculation: `(${regularHours} + ${seasonalHours}) * 4 * $20 = $${combined90.summary.staff_hours_saved * 4 * 20}`
        });
        
        setCalculatedCombinedData({
          monthly: combined30,
          quarterly: combined90
        });
      } catch (err) {
        console.error('Failed to fetch combined data in frontend:', err);
      }
    };
    
    // Always fetch combined data on mount or when menu data changes
    fetchCombinedData();
  }, [monthlyData, quarterlyData]); // Re-fetch when menu changes

  // Calculate savings with rounded hours for display cards
  const monthlyHours = Math.round(monthlyData?.summary?.staff_hours_saved || 0);
  const quarterlyHours = Math.round(quarterlyData?.summary?.staff_hours_saved || 0);
  
  const monthlySavings = monthlyHours * hourlyRate;
  const quarterlySavings = quarterlyHours * hourlyRate;
  
  // Use the best available combined data for annual projection
  // Priority: 1. Combined from parent, 2. Frontend calculated, 3. Current menu data
  const combinedQuarterlyHours = Math.round(
    combinedQuarterlyData?.summary?.staff_hours_saved || 
    calculatedCombinedData.quarterly?.summary?.staff_hours_saved ||
    quarterlyData?.summary?.staff_hours_saved || 
    0
  );
    
  const annualProjectedSavings = combinedQuarterlyHours * 4 * hourlyRate;
  const annualHours = combinedQuarterlyHours * 4;
  
  // Debug logging
  console.log('Annual Projection Final:', {
    fromParent: combinedQuarterlyData?.summary?.staff_hours_saved,
    fromFrontend: calculatedCombinedData.quarterly?.summary?.staff_hours_saved,
    fallback: quarterlyData?.summary?.staff_hours_saved,
    final: combinedQuarterlyHours,
    annualSavings: annualProjectedSavings
  });

  // Progress calculation (30-day as % of 90-day)
  const revenueProgress = ((monthlyData?.summary?.revenue_generated || 0) / 
                          (quarterlyData?.summary?.revenue_generated || 1)) * 100;
  const ordersProgress = ((monthlyData?.summary?.total_orders || 0) / 
                         (quarterlyData?.summary?.total_orders || 1)) * 100;

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          Extended Performance
          <Sparkles className="w-5 h-5 text-[#FDB913] animate-pulse" />
        </h2>

        <div className="relative group">
          <Info className="w-5 h-5 text-gray-400 cursor-help" />
          <div className="absolute bottom-full right-0 mb-2 px-4 py-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 w-80 shadow-xl">
            <div className="space-y-2">
              <p className="font-semibold text-[#FDB913]">How we calculate savings:</p>
              <p>Each order saves ~2 minutes through AI-powered recommendations and streamlined operations.</p>
              <p className="mt-2">
                Labor costs calculated at <span className="font-semibold">${hourlyRate}/hour</span> (industry average).
              </p>
            </div>
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      </motion.div>

      {/* Performance Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* 30-Day Card */}
        {monthlyData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={controls}
            transition={{ delay: 0.1 }}
            whileHover={{ scale: 1.01 }}
            className="relative overflow-hidden bg-white rounded-2xl p-6 border border-gray-200 hover:border-gray-300 transition-all duration-300 shadow-sm border-l-4 border-l-blue-500"
          >
            <div className="relative z-10">
              {/* Period Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">30-Day Performance</h3>
                  <p className="text-sm text-gray-500">{formatDateRange(monthlyData.date_range?.start, monthlyData.date_range?.end)}</p>
                </div>
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <DollarSign className="w-3 h-3" /> Revenue
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    <AnimatedCounter value={monthlyData.summary?.revenue_generated} prefix="$" />
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Package className="w-3 h-3" /> Orders
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    <AnimatedCounter value={monthlyData.summary?.total_orders} />
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-500">AOV</p>
                  <p className="text-xl font-bold text-gray-900">
                    <AnimatedCounter value={monthlyData.summary?.avg_order_value} prefix="$" decimals={2} />
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-500">Hours Saved</p>
                  <p className="text-xl font-bold text-gray-900">
                    <AnimatedCounter value={monthlyHours} suffix="h" />
                  </p>
                </div>
              </div>

              {/* Savings Highlight */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Savings</span>
                  <motion.span
                    className="text-2xl font-bold text-green-600"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.5, type: "spring" }}
                  >
                    <AnimatedCounter value={monthlySavings} prefix="$" duration={1.5} />
                  </motion.span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* 90-Day Card */}
        {quarterlyData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={controls}
            transition={{ delay: 0.2 }}
            whileHover={{ scale: 1.01 }}
            className="relative overflow-hidden bg-white rounded-2xl p-6 border border-gray-200 hover:border-gray-300 transition-all duration-300 shadow-sm border-l-4 border-l-purple-500"
          >
            <div className="relative z-10">
              {/* Period Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">90-Day Performance</h3>
                  <p className="text-sm text-gray-500">{formatDateRange(quarterlyData.date_range?.start, quarterlyData.date_range?.end)}</p>
                </div>
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <DollarSign className="w-3 h-3" /> Revenue
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    <AnimatedCounter value={quarterlyData.summary?.revenue_generated} prefix="$" />
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Package className="w-3 h-3" /> Orders
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    <AnimatedCounter value={quarterlyData.summary?.total_orders} />
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-500">AOV</p>
                  <p className="text-xl font-bold text-gray-900">
                    <AnimatedCounter value={quarterlyData.summary?.avg_order_value} prefix="$" decimals={2} />
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-500">Hours Saved</p>
                  <p className="text-xl font-bold text-gray-900">
                    <AnimatedCounter value={quarterlyHours} suffix="h" />
                  </p>
                </div>
              </div>

              {/* Savings Highlight */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Savings</span>
                  <motion.span
                    className="text-2xl font-bold text-green-600"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.6, type: "spring" }}
                  >
                    <AnimatedCounter value={quarterlySavings} prefix="$" duration={1.5} />
                  </motion.span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Hero Annual Savings Card */}
      {(combinedQuarterlyData || quarterlyData) && annualProjectedSavings > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, type: "spring" }}
          className="relative overflow-hidden bg-gradient-to-r from-[#FDB913]/10 to-[#FDB913]/5 rounded-2xl p-8 border border-[#FDB913]/30 shadow-sm"
          style={{ marginTop: '2rem' }}
        >
          {/* Content */}
          <div className="relative z-10">
            <div className="flex items-center justify-center gap-6">
              {/* Left side - Cake Image (vertically centered) */}
              <div className="hidden lg:flex items-center flex-shrink-0">
                <img
                  src="/rainbow-cake.png"
                  alt="Celebration"
                  className="w-72 h-72 object-contain opacity-95 drop-shadow-2xl -mt-4"
                />
              </div>

              {/* Right side - Content (moved closer to image) */}
              <div className="flex-1 max-w-2xl space-y-4">

              {/* Title */}
              <div className="text-center">
                <h3 className="text-sm font-medium text-gray-600 uppercase tracking-wider mb-2">
                  Annual Savings Projection
                </h3>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                  className="text-5xl font-bold text-[#FDB913] mb-2"
                >
                  <AnimatedCounter value={annualProjectedSavings} prefix="$" duration={2} />
                </motion.div>
                <p className="text-gray-500">per year</p>
              </div>

              {/* Details */}
              <div className="flex items-center justify-center gap-8 pt-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">
                    <AnimatedCounter value={annualHours} suffix=" hours" />
                  </p>
                  <p className="text-sm text-gray-500">Time Saved Annually</p>
                </div>
                <div className="w-px h-12 bg-gray-200" />
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">
                    <AnimatedCounter value={annualHours / 40} decimals={1} suffix=" weeks" />
                  </p>
                  <p className="text-sm text-gray-500">Of Full-Time Work</p>
                </div>
              </div>

              {/* ROI Message */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="text-sm text-gray-600 max-w-3xl mx-auto pt-4 border-t border-gray-200 text-center"
              >
                💡 Dine AI pays for itself while giving your staff {Math.round(annualHours)} hours back<br />
                to focus on what matters most - creating exceptional customer experiences.
              </motion.p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

    </div>
  );
}